import type { ChipColor } from '@/types/chip';
import type { ServiceAuthorisationRecord } from '@/types/entities';
import { Box, Typography, Paper, Grid, Chip, IconButton, Divider, Button, Alert, Snackbar } from '@mui/material';
import { VerifiedUser, CalendarMonth, Close, Person, Business, Schedule, WarningAmber } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';

const STATUS_COLORS: Record<string, ChipColor> = {
  Pending: 'warning',
  Active: 'success',
  Expired: 'error',
  Revoked: 'default',
};

export const ServiceAuthorisationDetail = () => {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/service-authorisations')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Service Authorisation"
            description="An approved tutoring service for a specific client. Authorises hours, validity period, and conditions."
            icon={<VerifiedUser />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #c62828, #d32f2f)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <VerifiedUser sx={{ fontSize: 48 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{record.tutorName || record.id?.substring(0, 12)}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.serviceName || '—'}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <VerifiedUser color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Authorisation Details</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Authorisation #</Typography>
                    <Typography variant="body1" fontWeight={500}>{record.id?.substring(0, 12) || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Service</Typography>
                    {record.service?.id ? (
                      <Button size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => navigate(`/services/${record.service.id}/show`)}>
                        {record.serviceName || record.service.id}
                      </Button>
                    ) : (
                      <Typography variant="body1">—</Typography>
                    )}
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="caption" color="text.secondary">Tutor</Typography>
                    <Typography variant="body1">{record.tutorName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Valid From</Typography>
                        <Typography variant="body1">{record.effectiveFrom || '—'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarMonth fontSize="small" color="action" />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Valid Until</Typography>
                        <Typography variant="body1">{record.effectiveTo || '—'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Conditions</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Tutor</Typography>
                    <Typography variant="body1">{record.tutorName || '—'}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Service</Typography>
                    <Typography variant="body1">{record.serviceName || '—'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <ValidityCountdown validFrom={record.effectiveFrom} validUntil={record.effectiveTo} status={record.status} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <AuthorisationTransitionActions record={record} snackbar={snackbar} setSnackbar={setSnackbar} />
            </Grid>
          </Grid>

          <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      );
    }} />
  );
};

const ValidityCountdown = ({ validFrom, validUntil, status }: { validFrom: string; validUntil: string; status: string }) => {
  const countdown = useMemo(() => {
    if (!validUntil) return null;

    const now = new Date();
    const expiry = new Date(validUntil);
    const start = validFrom ? new Date(validFrom) : null;
    const totalDays = start ? Math.ceil((expiry.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) : null;
    const remainingDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (status === 'Expired' || status === 'Revoked') {
      return { remainingDays: 0, totalDays, percentage: 0, expired: true };
    }

    const percentage = totalDays && totalDays > 0 ? Math.max(0, Math.min(100, ((totalDays - remainingDays) / totalDays) * 100)) : 0;

    return { remainingDays: Math.max(0, remainingDays), totalDays, percentage, expired: remainingDays <= 0 };
  }, [validFrom, validUntil, status]);

  if (!countdown) return null;

  const urgencyColor = countdown.expired ? 'error' : countdown.remainingDays <= 30 ? 'warning' : 'success';

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Schedule color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Validity Period</Typography>
        {countdown.expired && <WarningAmber color="error" />}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Chip
          label={countdown.expired ? 'Expired' : `${countdown.remainingDays} days remaining`}
          color={urgencyColor as ChipColor}
          icon={countdown.expired ? <WarningAmber /> : undefined}
        />
        {countdown.totalDays && (
          <Typography variant="body2" color="text.secondary">
            of {countdown.totalDays} total days ({Math.round(countdown.percentage)}% elapsed)
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

const AuthorisationTransitionActions = ({ record, snackbar, setSnackbar }: {
  record: ServiceAuthorisationRecord;
  snackbar: { open: boolean; message: string; severity: 'success' | 'error' };
  setSnackbar: React.Dispatch<React.SetStateAction<{ open: boolean; message: string; severity: 'success' | 'error' }>>;
}) => {
  const transitions: { event: string; label: string; color: 'success' | 'warning' | 'error'; from: string[] }[] = [
    { event: 'submit', label: 'Submit for Review', color: 'success', from: ['Draft'] },
    { event: 'approve', label: 'Approve', color: 'success', from: ['Pending Review', 'Under Review'] },
    { event: 'reject', label: 'Reject', color: 'error', from: ['Pending Review', 'Under Review'] },
    { event: 'revoke', label: 'Revoke', color: 'error', from: ['Active'] },
  ];

  const available = transitions.filter(t => t.from.includes(record.status));

  if (available.length === 0) return null;

  const handleTransition = async (event: string) => {
    try {
      const res = await fetch(`/api/v1/workflow-instances/${record.id}/transition`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event }),
      });
      if (!res.ok) throw new Error('Transition failed');
      setSnackbar({ open: true, message: 'Status updated', severity: 'success' });
      window.location.reload();
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Transition failed', severity: 'error' });
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>Actions</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {available.map(t => (
          <Button key={t.event} variant="outlined" color={t.color} onClick={() => handleTransition(t.event)}>
            {t.label}
          </Button>
        ))}
      </Box>
    </Paper>
  );
};
